import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import AdminShell from "./AdminShell";
import { firestore, storage } from "../firebase";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";

/* ---------- UI ---------- */

const Grid = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: 1.15fr 0.85fr;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  padding: 14px;
  background: #fff;
`;

const Label = styled.div`
  font-weight: 900;
  margin: 10px 0 6px;
  color: #111827;
`;

const Input = styled.input`
  width: 100%;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  padding: 12px;
  font-weight: 800;
`;

const Textarea = styled.textarea`
  width: 100%;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  padding: 12px;
  font-weight: 800;
  min-height: 120px;
  resize: vertical;
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
  margin-top: 12px;
`;

const Btn = styled.button`
  appearance: none;
  border: 1px solid rgba(15, 118, 110, 0.22);
  background: #0f766e;
  color: #fff;
  font-weight: 950;
  border-radius: 999px;
  padding: 10px 14px;
  cursor: pointer;

  &:disabled {
    opacity: 0.65;
    cursor: wait;
    transform: none;
  }

  &:hover {
    filter: brightness(1.03);
    transform: translateY(-1px);
  }
`;

const Ghost = styled(Btn)`
  background: #fff;
  color: #111827;
  border: 1px solid #e5e7eb;

  &:hover {
    background: #f9fafb;
  }
`;

const Danger = styled(Btn)`
  background: rgba(233, 78, 27, 0.08);
  color: #e94e1b;
  border: 1px solid rgba(233, 78, 27, 0.28);
`;

const Item = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 12px;
  display: grid;
  gap: 10px;
`;

const ItemTop = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const Thumb = styled.img`
  width: 84px;
  height: 64px;
  object-fit: cover;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  background: #000;
`;

const Muted = styled.div`
  color: #6b7280;
  font-weight: 800;
  font-size: 12px;
  line-height: 1.45;
`;

const Pill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 950;
  color: #0f766e;
  background: rgba(15, 118, 110, 0.1);
  border: 1px solid rgba(15, 118, 110, 0.18);
  padding: 7px 10px;
  border-radius: 999px;
  font-size: 12px;
`;

const SmallNote = styled.div`
  margin-top: 10px;
  color: #6b7280;
  font-weight: 800;
  font-size: 12px;
`;

const PreviewBox = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  overflow: hidden;
`;

const PreviewTop = styled.div`
  padding: 10px 12px;
  font-weight: 950;
  background: rgba(0, 0, 0, 0.02);
  border-bottom: 1px solid #e5e7eb;
`;

const PreviewBody = styled.div`
  padding: 12px;
  font-size: 14px;
  line-height: 1.65;
`;

const Hr = styled.div`
  height: 1px;
  background: #e5e7eb;
  margin: 12px 0;
`;

/* ---------- Helpers ---------- */

function makeStoragePath(file) {
  const safeName = (file?.name || "news")
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9.\-_]/g, "");
  return `news/${Date.now()}-${safeName}`;
}

function toMillis(ts) {
  return ts?.toMillis?.() || 0;
}

function pickImageUrl(it) {
  return it?.imageUrl || it?.image1 || "";
}

/* ---------- Component ---------- */

export default function AdminNews() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // formfält (håll det enkelt)
  const [title, setTitle] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [content, setContent] = useState(""); // HTML/text (du verkar spara HTML i Firestore)
  const [file, setFile] = useState(null);

  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : ""), [file]);

  const load = async () => {
    const snap = await getDocs(collection(firestore, "news"));
    const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

    // sort: createdAt desc, annars fallback
    list.sort((a, b) => {
      const at = toMillis(a.createdAt);
      const bt = toMillis(b.createdAt);
      if (bt !== at) return bt - at;
      return String(b.id).localeCompare(String(a.id));
    });

    setItems(list);
  };

  useEffect(() => {
    load();
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reset = () => {
    setEditingId(null);
    setTitle("");
    setExcerpt("");
    setContent("");
    setFile(null);
  };

  const startEdit = (it) => {
    setEditingId(it.id);
    setTitle(it.title || "");
    setExcerpt(it.excerpt || it.summary || "");
    setContent(it.content || it.text || "");
    setFile(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const uploadImageIfNeeded = async () => {
    if (!file) return { imageUrl: null, storagePath: null };

    const path = makeStoragePath(file);
    const r = ref(storage, path);
    await uploadBytes(r, file);
    const url = await getDownloadURL(r);
    return { imageUrl: url, storagePath: path };
  };

  const save = async () => {
    if (!title.trim()) return alert("Skriv en titel.");
    if (!editingId && !file && !content.trim())
      return alert("Lägg till innehåll och/eller en bild.");

    setBusy(true);
    try {
      const uploaded = await uploadImageIfNeeded();

      if (editingId) {
        const old = items.find((x) => x.id === editingId);

        // om ny bild laddas upp: radera gamla storagefilen om den finns
        if (uploaded.storagePath && old?.storagePath) {
          try {
            await deleteObject(ref(storage, old.storagePath));
          } catch {
            // ignore
          }
        }

        const payload = {
          title: title.trim(),
          excerpt: excerpt.trim() || null,
          content: content || "",
          updatedAt: serverTimestamp(),
        };

        if (uploaded.imageUrl) {
          payload.imageUrl = uploaded.imageUrl;
          payload.image1 = uploaded.imageUrl; // ✅ bakåtkomp med ditt fält
          payload.storagePath = uploaded.storagePath;
        }

        await updateDoc(doc(firestore, "news", editingId), payload);
      } else {
        await addDoc(collection(firestore, "news"), {
          title: title.trim(),
          excerpt: excerpt.trim() || null,
          content: content || "",
          imageUrl: uploaded.imageUrl || null,
          image1: uploaded.imageUrl || null, // ✅ bakåtkomp
          storagePath: uploaded.storagePath || null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }

      reset();
      await load();
      alert("Sparat!");
    } catch (e) {
      alert(e?.message || "Kunde inte spara nyhet.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm("Ta bort nyheten?")) return;

    setBusy(true);
    try {
      await deleteDoc(doc(firestore, "news", it.id));

      if (it.storagePath) {
        try {
          await deleteObject(ref(storage, it.storagePath));
        } catch {
          // ignore
        }
      }

      if (editingId === it.id) reset();
      await load();
    } catch (e) {
      alert(e?.message || "Kunde inte ta bort nyhet.");
    } finally {
      setBusy(false);
    }
  };

  const editingItem = editingId ? items.find((x) => x.id === editingId) : null;

  return (
    <AdminShell title="Nyheter (upload)">
      <Grid>
        {/* FORM */}
        <Card>
          <h3 style={{ margin: 0 }}>{editingId ? "Redigera nyhet" : "Skapa nyhet"}</h3>

          <Label>Titel</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Rubrik..." />

          <Label>Ingress (valfri)</Label>
          <Input
            value={excerpt}
            onChange={(e) => setExcerpt(e.target.value)}
            placeholder="Kort sammanfattning..."
          />

          <Label>Innehåll (HTML eller text)</Label>
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="<p>Din text...</p>"
          />

          <Label>Bild (ladda upp)</Label>
          <Input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} />

          {(previewUrl || pickImageUrl(editingItem)) ? (
            <div style={{ marginTop: 10 }}>
              <Thumb
                src={previewUrl || pickImageUrl(editingItem)}
                alt="Preview"
                style={{ width: 240, height: 150 }}
              />
            </div>
          ) : null}

          <Row>
            <Btn type="button" onClick={save} disabled={busy}>
              {busy ? "Sparar…" : editingId ? "Spara" : "Skapa"}
            </Btn>
            <Ghost type="button" onClick={reset} disabled={busy}>
              Rensa
            </Ghost>
          </Row>

          <SmallNote>
            Sparas i <code>news</code> som: <b>title</b>, <b>excerpt</b>, <b>content</b>, <b>imageUrl</b> (och
            även <b>image1</b> för kompatibilitet), samt <b>storagePath</b>, <b>createdAt</b>, <b>updatedAt</b>.
          </SmallNote>

          <Hr />

          <PreviewBox>
            <PreviewTop>Förhandsvisning (enkel)</PreviewTop>
            <PreviewBody>
              <div style={{ fontWeight: 950, fontSize: 16 }}>{title || "Titel…"}</div>
              {excerpt ? <div style={{ color: "#6b7280", marginTop: 6 }}>{excerpt}</div> : null}
              {pickImageUrl({ imageUrl: previewUrl || pickImageUrl(editingItem) }) ? (
                <div style={{ marginTop: 12 }}>
                  <img
                    src={previewUrl || pickImageUrl(editingItem)}
                    alt="preview"
                    style={{ width: "100%", borderRadius: 14, border: "1px solid #e5e7eb" }}
                  />
                </div>
              ) : null}
              {content ? (
                <div style={{ marginTop: 12 }}>
                  <div style={{ color: "#6b7280", fontWeight: 900, fontSize: 12, marginBottom: 6 }}>
                    Innehåll:
                  </div>
                  <div
                    style={{ border: "1px solid #e5e7eb", borderRadius: 14, padding: 12 }}
                    dangerouslySetInnerHTML={{ __html: content }}
                  />
                </div>
              ) : null}
            </PreviewBody>
          </PreviewBox>
        </Card>

        {/* LISTA */}
        <Card>
          <h3 style={{ margin: 0 }}>Lista</h3>
          <div style={{ height: 10 }} />

          {items.length === 0 ? (
            <Muted>Inga nyheter ännu.</Muted>
          ) : (
            items.map((it) => (
              <Item key={it.id}>
                <ItemTop>
                  <Thumb src={pickImageUrl(it) || "data:image/gif;base64,R0lGODlhAQABAAAAACw="} alt="Nyhet" />
                  <div style={{ display: "grid", gap: 6 }}>
                    <div style={{ fontWeight: 950, color: "#111827" }}>{it.title || "(saknar titel)"}</div>
                    <Muted>{it.excerpt || it.summary || ""}</Muted>
                    <Pill>
                      {toMillis(it.createdAt)
                        ? new Date(toMillis(it.createdAt)).toLocaleString()
                        : "saknar createdAt"}
                    </Pill>
                  </div>
                </ItemTop>

                <Row>
                  <Ghost type="button" onClick={() => startEdit(it)} disabled={busy}>
                    Redigera
                  </Ghost>
                  <Danger type="button" onClick={() => remove(it)} disabled={busy}>
                    Ta bort
                  </Danger>
                </Row>
              </Item>
            ))
          )}
        </Card>
      </Grid>
    </AdminShell>
  );
}

